import { useCallback, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { inventarioApi, isOfflineError, cache } from '../services/inventarioApi';
import { offlineQueue } from '../services/offlineQueue';
import { normalizarFechaISO } from '../../../utils/horaLocal';

const ENTIDAD = 'limpiezas';
const ENT_MOV = 'movimientos';
const QUEUE_KEY = 'inventario_offline_queue';

function hasPendingLimpiezaCreate(procesoId) {
  try {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    return queue.some(
      (req) =>
        req.entidad === 'limpiezas' &&
        req.method === 'POST' &&
        !req.body?._op &&
        req.body?.id === procesoId
    );
  } catch {
    return false;
  }
}

function removeQueuedLimpiezaCreate(procesoId) {
  try {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    const filtered = queue.filter(
      (req) =>
        !(
          req.entidad === 'limpiezas' &&
          req.method === 'POST' &&
          !req.body?._op &&
          req.body?.id === procesoId
        )
    );
    localStorage.setItem(QUEUE_KEY, JSON.stringify(filtered));
  } catch {
    /* ignore */
  }
}

function buildOfflineLimpieza(payload, obtenerPrecioPromedioHasta, { origen = 'MANUAL' } = {}) {
  const procesoId = uuid();
  const transactionID = uuid();
  const fecha = payload.fecha;
  const materialOrigenId = payload.materialOrigenId;
  const cantidadDescontadaKg = parseFloat(payload.cantidadDescontadaKg);
  const resultados = payload.resultados || [];
  const metodoPago = payload.metodoPago || 'EFECTIVO';

  const precioFijado = obtenerPrecioPromedioHasta
    ? obtenerPrecioPromedioHasta(materialOrigenId, fecha)
    : 0;
  const pf = parseFloat(Number(precioFijado).toFixed(5));

  const movs = [
    {
      id: `temp_${Date.now()}_${uuid()}`,
      productoId: materialOrigenId,
      cantidad: cantidadDescontadaKg,
      precio: pf,
      tipo: 'SALIDA',
      subTipo: 'LIMPIEZA_ORIGEN',
      transactionID,
      metodoPago: null,
      precioFijado: pf,
      fecha,
      anulado: false,
      origenOperacion: 'LIMPIEZA',
    },
  ];

  for (const r of resultados) {
    movs.push({
      id: `temp_${Date.now()}_${uuid()}`,
      productoId: r.materialDestinoId,
      cantidad: parseFloat(r.cantidadKg),
      precio: parseFloat(r.precioCompraKg),
      tipo: 'ENTRADA',
      subTipo: 'LIMPIEZA_RESULTADO',
      transactionID,
      metodoPago,
      precioFijado: null,
      fecha,
      anulado: false,
      origenOperacion: 'LIMPIEZA',
    });
  }

  const importeTotalCompra = resultados.reduce(
    (a, r) => a + parseFloat(r.cantidadKg) * parseFloat(r.precioCompraKg),
    0
  );

  const queued = {
    ...payload,
    id: procesoId,
    localId: procesoId,
    transactionID,
  };

  const proceso = {
    id: procesoId,
    localId: procesoId,
    transactionID,
    fecha,
    materialOrigenId,
    cantidadDescontadaKg,
    resultados,
    trabajadorDescripcion: payload.trabajadorDescripcion || '',
    observacion: payload.observacion || '',
    metodoPago,
    motivo: payload.motivo,
    importeTotalCompra,
    estado: 'ACTIVO',
    origen,
    pendingSync: true,
  };

  return { proceso, movs, body: queued };
}

export function useLimpiezas({
  onRefreshMovimientos,
  onRefreshGastos,
  setMovimientos,
  obtenerPrecioPromedioHasta,
  marcarGastoConvertidoLocal,
} = {}) {
  const [limpiezas, setLimpiezas] = useState([]);
  const [loading, setLoading] = useState(true);

  const appendMovimientos = useCallback(
    (movs) => {
      if (!setMovimientos || !movs?.length) return;
      setMovimientos((prev) => {
        const updated = [...prev, ...movs];
        cache.set(ENT_MOV, updated);
        return updated;
      });
    },
    [setMovimientos]
  );

  const anularMovsPorTransactionID = useCallback(
    (transactionID) => {
      if (!setMovimientos || !transactionID) return;
      setMovimientos((prev) => {
        const updated = prev.map((m) =>
          m.transactionID === transactionID ? { ...m, anulado: true } : m
        );
        cache.set(ENT_MOV, updated);
        return updated;
      });
    },
    [setMovimientos]
  );

  const fetchLimpiezas = useCallback(async (params) => {
    try {
      const data = await inventarioApi.listarLimpiezas(params);
      const list = Array.isArray(data) ? data : [];
      setLimpiezas(list);
      cache.set(ENTIDAD, list);
    } catch (e) {
      if (isOfflineError(e)) setLimpiezas(cache.get(ENTIDAD) || []);
      else console.error('Error fetch limpiezas', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLimpiezas();
  }, [fetchLimpiezas]);

  const refreshDependientes = useCallback(async () => {
    await Promise.all([
      fetchLimpiezas(),
      onRefreshMovimientos?.(),
      onRefreshGastos?.(),
    ]);
  }, [fetchLimpiezas, onRefreshMovimientos, onRefreshGastos]);

  const marcarProcesoAnuladoLocal = useCallback((id) => {
    setLimpiezas((prev) => {
      const updated = prev.map((l) =>
        (l.id || l._id) === id ? { ...l, estado: 'ANULADO' } : l
      );
      cache.set(ENTIDAD, updated);
      return updated;
    });
  }, []);

  const crearLimpieza = async (body) => {
    try {
      const doc = await inventarioApi.crearLimpieza(body);
      setLimpiezas((prev) => {
        const updated = [...prev, doc];
        cache.set(ENTIDAD, updated);
        return updated;
      });
      await refreshDependientes();
      return doc;
    } catch (e) {
      if (!isOfflineError(e)) throw e;
      if (!obtenerPrecioPromedioHasta) {
        console.error('crearLimpieza offline requiere obtenerPrecioPromedioHasta');
        throw e;
      }
      const { proceso, movs, body: queued } = buildOfflineLimpieza(
        body,
        obtenerPrecioPromedioHasta
      );
      setLimpiezas((prev) => {
        const updated = [...prev, proceso];
        cache.set(ENTIDAD, updated);
        return updated;
      });
      appendMovimientos(movs);
      offlineQueue.enqueue({ method: 'POST', entidad: ENTIDAD, body: queued });
      return proceso;
    }
  };

  const anularLimpieza = async (id) => {
    const proc = limpiezas.find((l) => (l.id || l._id) === id);
    const transactionID = proc?.transactionID;

    try {
      const doc = await inventarioApi.anularLimpieza(id);
      await refreshDependientes();
      return doc;
    } catch (e) {
      if (!isOfflineError(e)) throw e;
      marcarProcesoAnuladoLocal(id);
      if (transactionID) anularMovsPorTransactionID(transactionID);
      if (hasPendingLimpiezaCreate(id)) {
        removeQueuedLimpiezaCreate(id);
      } else {
        offlineQueue.enqueue({ method: 'DELETE', entidad: ENTIDAD, id });
      }
      return null;
    }
  };

  const convertirGastoALimpieza = async (gastoId, body) => {
    try {
      const result = await inventarioApi.convertirGastoALimpieza(gastoId, body);
      await refreshDependientes();
      return result;
    } catch (e) {
      if (!isOfflineError(e)) throw e;
      if (!obtenerPrecioPromedioHasta) {
        console.error('convertirGastoALimpieza offline requiere obtenerPrecioPromedioHasta');
        throw e;
      }
      const { proceso, movs, body: queued } = buildOfflineLimpieza(
        body,
        obtenerPrecioPromedioHasta,
        { origen: 'GASTO' }
      );
      marcarGastoConvertidoLocal?.(gastoId, {
        motivo: body.motivo,
        limpiezaLocalId: proceso.id,
      });
      setLimpiezas((prev) => {
        const updated = [...prev, proceso];
        cache.set(ENTIDAD, updated);
        return updated;
      });
      appendMovimientos(movs);
      offlineQueue.enqueue({
        method: 'POST',
        entidad: ENTIDAD,
        body: { _op: 'convertir_desde_gasto', gastoId, ...queued },
      });
      return { proceso, gastoId };
    }
  };

  const obtenerLimpiezasPorFecha = useCallback(
    (fechaISO) => {
      if (!fechaISO) return [];
      const dia = normalizarFechaISO(fechaISO);
      return limpiezas.filter((l) => {
        if (l.estado === 'ANULADO') return false;
        return normalizarFechaISO(l.fecha) === dia;
      });
    },
    [limpiezas]
  );

  return {
    limpiezas,
    loading,
    fetchLimpiezas,
    crearLimpieza,
    anularLimpieza,
    convertirGastoALimpieza,
    obtenerLimpiezasPorFecha,
  };
}
